import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ProductionOrderView,
  STAGES,
  inr,
  linkAccept,
  linkCounter,
  linkOrder,
  linkOtpSend,
  linkOtpVerify,
  linkPresign,
  linkUpdate,
  shortDate,
  stageLabel,
  uploadTo,
} from "../../services/partnerNetworkApi";
import { LANGS, LinkLang, asLang, linkT } from "./linkText";
import "./partner-link.scss";

type Sheet = null | "counter" | "problem";

const LANG_KEY = "hkp.partner.lang";

/**
 * Mode C: one phone-width screen for a partner with no HKP account. No login, no menus. Accept,
 * counter, or send an update. Everything sent here belongs to the designer, and the footer says so.
 */
const PartnerLinkPage = () => {
  const { token } = useParams<{ token: string }>();
  const [order, setOrder] = useState<ProductionOrderView | null>(null);
  const [error, setError] = useState<{ code?: string; message?: string } | null>(null);
  const [needsOtp, setNeedsOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [lang, setLang] = useState<LinkLang>(() => {
    try {
      return asLang(localStorage.getItem(LANG_KEY));
    } catch {
      return "en";
    }
  });
  const [langChosen, setLangChosen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [counter, setCounter] = useState({ rate: "", quantity: "", dueDate: "" });
  const [problem, setProblem] = useState("");
  const [pieces, setPieces] = useState(0);
  const [recording, setRecording] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const t = (key: string, vars: Record<string, string> = {}) => linkT(lang, key, vars);
  const designer = order?.designer || "the designer";

  const apply = useCallback(
    (res: { success: boolean; data: ProductionOrderView; code?: string; message?: string; status?: number }) => {
      if (res.success && res.data) {
        setOrder(res.data);
        setPieces(res.data.piecesDone || 0);
        setError(null);
        setNeedsOtp(false);
        if (!langChosen) {
          try {
            if (!localStorage.getItem(LANG_KEY)) setLang(asLang(res.data.language));
          } catch {
            setLang(asLang(res.data.language));
          }
        }
        return true;
      }
      if (res.code === "OTP_REQUIRED") {
        setNeedsOtp(true);
        return false;
      }
      if (["LINK_REVOKED", "LINK_EXPIRED", "LINK_NOT_FOUND"].includes(res.code || "") || res.status === 404) {
        setError({ code: res.code || "LINK_NOT_FOUND" });
        return false;
      }
      flash(res.message || "Something went wrong");
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [langChosen]
  );

  useEffect(() => {
    linkOrder(token).then(apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  };

  const chooseLang = (id: LinkLang) => {
    setLang(id);
    setLangChosen(true);
    try {
      localStorage.setItem(LANG_KEY, id);
    } catch {
      /* private mode: the choice lasts this visit */
    }
  };

  const run = async (call: () => Promise<any>, done?: string) => {
    setBusy(true);
    const res = await call();
    setBusy(false);
    if (apply(res) && done) {
      flash(done);
      setSheet(null);
    }
  };

  const sendFiles = async (files: FileList | null, kind: "PHOTO" | "NOTE", text?: string) => {
    if (!files || !files.length) return;
    setBusy(true);
    const refs: { key: string; name: string; contentType: string }[] = [];
    for (const file of Array.from(files)) {
      const type = file.type || "image/jpeg";
      const signed = await linkPresign(token, file.name || "photo.jpg", type);
      if (!signed.success || !(await uploadTo(signed.data.uploadUrl, file, type))) {
        setBusy(false);
        flash(signed.message || "Upload failed. Try again.");
        return;
      }
      refs.push({ key: signed.data.key, name: file.name, contentType: type });
    }
    const res = await linkUpdate(token, { kind, text, files: refs });
    setBusy(false);
    if (apply(res)) flash(t("Sent"));
  };

  const toggleVoice = async () => {
    if (recording && recorder.current) {
      recorder.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => chunks.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
        const file = new File([blob], "voice-note.webm", { type: blob.type });
        const list = { 0: file, length: 1, item: () => file } as unknown as FileList;
        sendFiles(list, "NOTE", "Voice note");
      };
      recorder.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      flash("Microphone not available");
    }
  };

  if (error) {
    const message =
      error.code === "LINK_REVOKED"
        ? "This link was switched off by the designer."
        : error.code === "LINK_EXPIRED"
        ? "This order is closed and its link has expired."
        : "This link doesn't work. Ask the designer for a new one.";
    return (
      <main className="plink plink--center">
        <div className="plink__brand">HKP</div>
        <p className="plink__error">{t(message)}</p>
      </main>
    );
  }

  if (needsOtp) {
    return (
      <main className="plink plink--center">
        <div className="plink__brand">HKP</div>
        <h1 className="plink__title">{t("Enter the code we sent to your phone")}</h1>
        {otpSent ? (
          <>
            <input
              className="plink__otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              aria-label="Code"
            />
            <button
              className="plink__btn plink__btn--primary"
              disabled={otp.length !== 4 || busy}
              onClick={() => run(() => linkOtpVerify(token, otp))}
            >
              {t("Continue")}
            </button>
          </>
        ) : (
          <button
            className="plink__btn plink__btn--primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const res = await linkOtpSend(token);
              setBusy(false);
              if (res.success) setOtpSent(true);
              else flash(res.message || "Could not send the code");
            }}
          >
            {t("Send code")}
          </button>
        )}
        {toast ? <div className="plink__toast" role="status">{toast}</div> : null}
      </main>
    );
  }

  if (!order) {
    return (
      <main className="plink plink--center" aria-busy="true">
        <div className="plink__brand">HKP</div>
        <div className="plink__skeleton" />
      </main>
    );
  }

  const negotiating = order.status === "SENT" || order.status === "COUNTERED";
  const myTurn = negotiating && order.turn === "PARTNER";
  const accepted = order.status === "ACCEPTED";
  const lastOffer = order.offers[order.offers.length - 1];
  const qty = order.agreedQuantity ?? lastOffer?.quantity ?? order.quantity;
  const rate = order.agreedRate ?? lastOffer?.rate ?? order.proposedRate;
  const due = order.agreedDueDate ?? lastOffer?.dueDate ?? order.dueDate;

  return (
    <main className="plink">
      <header className="plink__top">
        <div className="plink__brand">HKP</div>
        <div className="plink__langs" role="group" aria-label="Language">
          {LANGS.map((l) => (
            <button
              key={l.id}
              className={`plink__lang${lang === l.id ? " is-on" : ""}`}
              aria-pressed={lang === l.id}
              onClick={() => chooseLang(l.id)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </header>

      <section className="plink__card">
        <p className="plink__eyebrow">{t("Order from {designer}", { designer })} · {order.code}</p>
        <h1 className="plink__title">{order.title}</h1>
        {order.specifications ? <p className="plink__spec">{order.specifications}</p> : null}
        {order.designRefs?.length ? (
          <div className="plink__refs">
            {order.designRefs.map((ref, i) =>
              !ref.url ? null : (ref.contentType || "").startsWith("image/") ? (
                <a key={i} href={ref.url} target="_blank" rel="noreferrer"><img src={ref.url} alt={ref.name || "Design"} /></a>
              ) : (
                <a key={i} className="plink__reffile" href={ref.url} target="_blank" rel="noreferrer">{ref.name || "PDF"}</a>
              )
            )}
          </div>
        ) : null}
        {order.lines?.length ? (
          <ul className="plink__lines">
            {order.lines.map((line, i) => (
              <li key={i}>
                <span>{[line.colour, line.size].filter(Boolean).join(" · ") || "—"}</span>
                <b>{line.quantity ?? "—"}</b>
              </li>
            ))}
          </ul>
        ) : null}
        <dl className="plink__facts">
          <div>
            <dt>{t("Pieces")}</dt>
            <dd>{qty ?? "—"}</dd>
          </div>
          <div>
            <dt>{t("Rate per piece")}</dt>
            <dd>{inr(rate)}</dd>
          </div>
          <div>
            <dt>{t("Due by")}</dt>
            <dd>{shortDate(due)}</dd>
          </div>
        </dl>
        {accepted ? <p className="plink__agreed">✓ {t("Agreed terms")}</p> : null}
        {order.status === "DECLINED" || order.status === "CANCELLED" ? (
          <p className="plink__agreed is-closed">{t(order.status === "DECLINED" ? "Declined" : "Cancelled")}</p>
        ) : null}
      </section>

      {myTurn ? (
        <div className="plink__actions">
          <button className="plink__btn plink__btn--primary" disabled={busy || order.readOnly}
            onClick={() => run(() => linkAccept(token), t("Order accepted"))}>
            {t("Accept")}
          </button>
          <button className="plink__btn" disabled={busy || order.readOnly}
            onClick={() => {
              setCounter({ rate: String(rate ?? ""), quantity: String(qty ?? ""), dueDate: due ? new Date(due).toISOString().slice(0, 10) : "" });
              setSheet("counter");
            }}>
            {t("Counter")}
          </button>
        </div>
      ) : null}
      {negotiating && !myTurn ? <p className="plink__wait">{t("Waiting for {designer} to reply", { designer })}</p> : null}

      {accepted && !order.readOnly ? (
        <section className="plink__card plink__update">
          <h2>{t("Stage")}</h2>
          <div className="plink__chips">
            {STAGES.filter((s) => s !== "ACCEPTED").map((s) => (
              <button
                key={s}
                className={`plink__chip${order.stage === s ? " is-on" : ""}${s === "STUCK" ? " is-warn" : ""}`}
                aria-pressed={order.stage === s}
                disabled={busy}
                onClick={() => run(() => linkUpdate(token, { kind: "STAGE", stage: s }), t("Sent"))}
              >
                {t(stageLabel(s))}
              </button>
            ))}
          </div>

          <h2>{t("Pieces done")}</h2>
          <div className="plink__stepper">
            <button aria-label="−" onClick={() => setPieces(Math.max(0, pieces - 1))} disabled={busy || pieces <= 0}>−</button>
            <output aria-live="polite">{pieces}<small> / {qty ?? "—"}</small></output>
            <button aria-label="+" onClick={() => setPieces(Math.min(qty ?? 9999, pieces + 1))} disabled={busy}>+</button>
            <button className="plink__save" disabled={busy || pieces === (order.piecesDone || 0)}
              onClick={() => run(() => linkUpdate(token, { kind: "PROGRESS", piecesDone: pieces }), t("Sent"))}>
              {t("Save")}
            </button>
          </div>

          <div className="plink__tiles">
            <button className="plink__tile" disabled={busy} onClick={() => fileInput.current?.click()}>
              <span aria-hidden="true">📷</span>
              {t("Add photos")}
            </button>
            <button className={`plink__tile${recording ? " is-rec" : ""}`} disabled={busy && !recording} onClick={toggleVoice}>
              <span aria-hidden="true">{recording ? "■" : "🎙"}</span>
              {recording ? t("Stop and send") : t("Voice note")}
            </button>
            <button className="plink__tile is-warn" disabled={busy} onClick={() => setSheet("problem")}>
              <span aria-hidden="true">⚠</span>
              {t("Problem / delay")}
            </button>
          </div>
          <input ref={fileInput} type="file" accept="image/*" capture="environment" multiple hidden
            onChange={(e) => {
              sendFiles(e.target.files, "PHOTO");
              e.target.value = "";
            }} />
        </section>
      ) : null}

      {order.updates?.length ? (
        <section className="plink__card">
          <h2>{t("Recent updates")}</h2>
          <ol className="plink__timeline">
            {[...order.updates].reverse().slice(0, 6).map((u) => (
              <li key={u.id}>
                <time>{shortDate(u.at)}</time>
                <span>
                  {u.kind === "STAGE" ? t(stageLabel(u.payload.stage)) : null}
                  {u.kind === "PROGRESS" ? `${u.payload.piecesDone} ${t("Pieces")}` : null}
                  {u.payload.text && u.kind !== "STAGE" ? ` ${u.payload.text}` : null}
                  {u.payload.files?.length ? ` · ${u.payload.files.length} 📎` : null}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <footer className="plink__footer">{t("Updates you send here are shared with {designer}.", { designer })}</footer>

      {sheet ? (
        <div className="plink__scrim" onClick={() => setSheet(null)}>
          <div className="plink__sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {sheet === "counter" ? (
              <>
                <label>
                  {t("Your rate per piece")}
                  <input inputMode="decimal" value={counter.rate} onChange={(e) => setCounter({ ...counter, rate: e.target.value })} />
                </label>
                <label>
                  {t("Quantity")}
                  <input inputMode="numeric" value={counter.quantity} onChange={(e) => setCounter({ ...counter, quantity: e.target.value })} />
                </label>
                <label>
                  {t("Date you can deliver")}
                  <input type="date" value={counter.dueDate} onChange={(e) => setCounter({ ...counter, dueDate: e.target.value })} />
                </label>
                <button className="plink__btn plink__btn--primary" disabled={busy}
                  onClick={() =>
                    run(() => linkCounter(token, {
                      rate: counter.rate ? Number(counter.rate) : undefined,
                      quantity: counter.quantity ? Number(counter.quantity) : undefined,
                      dueDate: counter.dueDate || undefined,
                    }), t("Sent"))
                  }>
                  {t("Send counter")}
                </button>
              </>
            ) : (
              <>
                <label>
                  {t("What is the problem?")}
                  <textarea rows={4} value={problem} onChange={(e) => setProblem(e.target.value)} />
                </label>
                <button className="plink__btn plink__btn--primary" disabled={busy || !problem.trim()}
                  onClick={() => run(() => linkUpdate(token, { kind: "DELAY", text: problem.trim() }), t("Sent")).then(() => setProblem(""))}>
                  {t("Send")}
                </button>
              </>
            )}
            <button className="plink__btn plink__btn--ghost" onClick={() => setSheet(null)}>{t("Cancel")}</button>
          </div>
        </div>
      ) : null}
      {toast ? <div className="plink__toast" role="status">{toast}</div> : null}
    </main>
  );
};

export default PartnerLinkPage;
